import React from 'react';

import type { ReferralInfo } from '../services/referralService';

import './ReferralProgram.css';

interface ReferralProgramProps {
  copied: boolean;
  error: string | null;
  info: ReferralInfo | null;
  onCopy: () => void;
}

const RewardPath: React.FC = () => (
  <ol aria-label="What you earn" className="refer-path">
    <li>
      <span aria-hidden="true" className="refer-path__step">1</span>
      <span className="refer-path__copy"><strong>+5 credits</strong> when a friend signs up</span>
    </li>
    <li aria-hidden="true" className="refer-path__line" />
    <li>
      <span aria-hidden="true" className="refer-path__step">2</span>
      <span className="refer-path__copy"><strong>+20 credits</strong> when they subscribe</span>
    </li>
  </ol>
);

const ReferralHero: React.FC<{ info: ReferralInfo | null }> = ({ info }) => (
  <section aria-labelledby="refer-hero-title" className="refer-hero">
    <div className="refer-hero__top">
      <div className="refer-hero__copy">
        <span className="refer-eyebrow">Your personal invite</span>
        <h3 id="refer-hero-title">Give friends a head start.</h3>
      </div>
      <dl aria-label="Referral summary" className="refer-stats">
        <div><dt>Friends referred</dt><dd>{info?.total_referred ?? '–'}</dd></div>
        <div><dt>Credits earned</dt><dd className="refer-stats__amber">{info?.credits_earned ?? '–'}</dd></div>
      </dl>
    </div>
    <RewardPath />
    <p className="refer-hero__limit">
      {info?.referrals_remaining === 0
        ? `You have reached the ${info.referral_limit}-friend referral limit.`
        : `You can refer up to ${info?.referral_limit ?? 10} friends with your link.`}
    </p>
  </section>
);

const LinkIcon: React.FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
    <path d="M8.5 11.5a3.2 3.2 0 0 0 4.6.1l2.6-2.6a3.2 3.2 0 0 0-4.5-4.5l-1.1 1" />
    <path d="M11.5 8.5a3.2 3.2 0 0 0-4.6-.1L4.3 11a3.2 3.2 0 0 0 4.5 4.5l1.1-1" />
  </svg>
);

const ReferralLinkOption: React.FC<Pick<ReferralProgramProps, 'copied' | 'info' | 'onCopy'>> = ({
  copied, info, onCopy,
}) => (
  <section aria-labelledby="refer-link-title" className="refer-option">
    <div className="refer-option__head">
      <span className="refer-option__icon"><LinkIcon /></span>
      <div className="refer-option__copy">
        <h4 id="refer-link-title">Share your link</h4>
        <p>Post it anywhere. Visits, signups and subscriptions are tracked to you.</p>
      </div>
    </div>
    <div className="refer-link">
      <span className="refer-link__url">{info?.referral_link || 'Loading…'}</span>
      <button
        className={`refer-link__copy${copied ? ' is-copied' : ''}`}
        disabled={!info?.referral_link || info.referrals_remaining === 0}
        onClick={onCopy}
        type="button"
      >
        {info?.referrals_remaining === 0 ? 'Limit reached' : copied ? '✓ Copied' : 'Copy link'}
      </button>
    </div>
  </section>
);

/** The Refer friends tab: what you earn, then your link. (Inviting from Gmail is its own window: Invite people.) */
export const ReferralProgram: React.FC<ReferralProgramProps> = ({ copied, error, info, onCopy }) => (
  <div className="refer-program">
    <ReferralHero info={info} />
    {error && <p className="refer-error" role="alert">{error}</p>}
    <h3 className="refer-section-label">Invite friends</h3>
    <ReferralLinkOption copied={copied} info={info} onCopy={onCopy} />
  </div>
);
